<template>
  <MediaPlayer
    v-if="isPlayableMedia"
    ref="player"
    :src="mediaSrc"
    :media-type="mediaType"
    :poster="posterSrc"
    :is-hls="isHls"
    :width="mediaWidth"
    :height="mediaHeight"
    :max-width="mediaMaxWidth"
    :min-width="mediaMinWidth"
    :max-height="mediaMaxHeight"
    :min-height="mediaMinHeight"
    :active="isCoordinated ? coordinatorActive : null"
    :muted="isCoordinated ? coordinatorMuted : false"
    :controls-style="videoControlsStyle"
    @play="onPlay"
    @volumechange="onVolumeChange"
  />
  <iColumn
    v-else-if="showImage"
    :width="mediaWidth"
    :height="mediaHeight"
    :max-width="mediaMaxWidth"
    :min-width="mediaMinWidth"
    :max-height="mediaMaxHeight"
    :min-height="mediaMinHeight"
  >
    <iImageContainer
      :src="mediaSrc"
      :alt="entity.title"
      :show-placeholder="editMode"
      :width="mediaWidth"
      :height="mediaHeight"
      :aspect-ratio="mediaAspectRatio"
      :crop-if-needed="mediaCropIfNeeded"
    />
  </iColumn>
</template>

<script>
import { StoryPageElementMixin } from "%/dynamic-component-editor/standard-pages/story-page/story-page-element-mixin";
import { OptionMediaResolverMixin } from "%/dynamic-component-editor/mixins/option-media-resolver-mixin";
import LayoutWidthHeightOptions from "%/dynamic-component-editor/bundles/layout-width-height-options";
import StoryMediaPlaybackOptions from "%/dynamic-component-editor/bundles/story-media-playback-options";
import Autoplay, { AUTOPLAY_WHEN_SCROLLED_INTO_VIEW } from "%/dynamic-component-editor/options/autoplay";
import MuteOnAutoplay from "%/dynamic-component-editor/options/mute-on-autoplay";
import VideoControlsStyle from "%/dynamic-component-editor/options/video-controls-style";
import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import LayoutHeight from "%/dynamic-component-editor/options/layout-height";
import MaxWidth from "%/dynamic-component-editor/options/max-width";
import MinWidth from "%/dynamic-component-editor/options/min-width";
import MaxHeight from "%/dynamic-component-editor/options/max-height";
import MinHeight from "%/dynamic-component-editor/options/min-height";
import MediaPlayer from "%/components/MediaPlayer.vue";
import { isRatioValue, aspectForRatio } from "%/dynamic-component-editor/options/aspect-ratios";
import { selectStoryMediaSegment, parseStorySegmentContent, segmentTypeOf } from "%/lib/story-media";
import { useAutoplayCoordinatorStore } from "%/stores/autoplay-coordinator-store";
import AutoplayError from "%/errors/autoplay-error";

const AUTOPLAY_VISIBILITY_THRESHOLDS = [0, 0.25, 0.5, 0.75, 1];
const READER_GESTURE_WINDOW_MS = 1000;
const READER_GESTURE_EVENTS = ["pointerdown", "keydown"];
let storyMediaInstanceSeq = 0;

export default {
  name: "StoryMedia",
  supportsRatioSize: true,
  components: { MediaPlayer },
  mixins: [StoryPageElementMixin, OptionMediaResolverMixin],
  optionBundles: [
    LayoutWidthHeightOptions,
    StoryMediaPlaybackOptions,
  ],
  elementPanelSettings: {
    displayName: "Story Media",
    displayIcon: "video",
    elementType: "element",
    elementGroup: "storyElements",
  },
  inject: {
    feedVideoAutoplay: { default: null },
  },
  data() {
    return {
      coordinatorActive: false,
      coordinatorMuted: true,
      coordinatorId: null,
      muteGroupKey: null,
      lastReaderGestureAt: 0,
      visibilityObserver: null,
    };
  },
  computed: {
    mediaSegment() {
      return selectStoryMediaSegment(this.entity);
    },
    mediaType() {
      return this.mediaSegment ? segmentTypeOf(this.mediaSegment) : null;
    },
    mediaContent() {
      return parseStorySegmentContent(this.mediaSegment);
    },
    mediaSrc() {
      const content = this.mediaContent;
      return content ? this.resolveOptionMedia(content.mediaId || content.src || "") : "";
    },
    posterSrc() {
      const content = this.mediaContent;
      if (!content) {
        return "";
      }
      if (content.coverImageSrc) {
        return content.coverImageSrc;
      }
      return this.resolveOptionMedia(content.coverImage || content.poster || "");
    },
    isPlayableMedia() {
      return (this.mediaType === "video" || this.mediaType === "audio") && Boolean(this.mediaSrc);
    },
    showImage() {
      if (this.editMode) {
        return true;
      }
      return this.mediaType === "image" && Boolean(this.mediaSrc);
    },
    isHls() {
      return /\.m3u8(\?|$)/.test(this.mediaSrc);
    },
    isCoordinated() {
      return !this.editMode && !this.isPreviewMode
        && this.mediaType === "video" && Boolean(this.mediaSrc)
        && (this.getOptionValue(Autoplay) === AUTOPLAY_WHEN_SCROLLED_INTO_VIEW || this.isInAutoplayFeed);
    },
    isInAutoplayFeed() {
      return typeof this.feedVideoAutoplay === "function" && this.feedVideoAutoplay();
    },
    videoControlsStyle() {
      return this.getOptionValue(VideoControlsStyle);
    },
    autoplayMuteDefault() {
      return this.getOptionValue(MuteOnAutoplay) !== false;
    },
    mediaWidth() {
      const value = this.getOptionValue(LayoutWidth);
      return isRatioValue(value) ? "auto" : value;
    },
    mediaHeight() {
      const value = this.getOptionValue(LayoutHeight);
      return isRatioValue(value) ? "auto" : value;
    },
    mediaAspectRatio() {
      const widthValue = this.getOptionValue(LayoutWidth);
      if (isRatioValue(widthValue)) {
        return aspectForRatio(widthValue);
      }
      const heightValue = this.getOptionValue(LayoutHeight);
      if (isRatioValue(heightValue)) {
        return aspectForRatio(heightValue);
      }
      return null;
    },
    mediaCropIfNeeded() {
      return isRatioValue(this.getOptionValue(LayoutWidth)) || isRatioValue(this.getOptionValue(LayoutHeight));
    },
    mediaMaxWidth() {
      return this.getOptionValue(MaxWidth);
    },
    mediaMinWidth() {
      return this.getOptionValue(MinWidth);
    },
    mediaMaxHeight() {
      return this.getOptionValue(MaxHeight);
    },
    mediaMinHeight() {
      return this.getOptionValue(MinHeight);
    },
  },
  watch: {
    isCoordinated: {
      handler(coordinated) {
        if (coordinated) {
          this.$nextTick(() => this.joinAutoplayCoordinator());
        } else {
          this.leaveAutoplayCoordinator();
        }
      },
      immediate: true,
    },
  },
  beforeUnmount() {
    this.leaveAutoplayCoordinator();
  },
  methods: {
    joinAutoplayCoordinator() {
      if (this.coordinatorId || !this.isCoordinated) {
        return;
      }
      const muteGroupKey = this.dynamicElement?.dynamicComponentId;
      if (!muteGroupKey) {
        throw new AutoplayError("Story Media cannot join autoplay without its owning component", {
          identifier: this.identifier,
        });
      }
      storyMediaInstanceSeq += 1;
      this.coordinatorId = `story-media-${storyMediaInstanceSeq}`;
      const coordinator = useAutoplayCoordinatorStore();
      coordinator.register(this.coordinatorId, {
        autoplay: AUTOPLAY_WHEN_SCROLLED_INTO_VIEW,
        muteGroupKey: String(muteGroupKey),
        defaultMuted: this.autoplayMuteDefault,
        setActive: value => {
          this.coordinatorActive = value;
        },
        setMuted: value => {
          this.coordinatorMuted = value;
        },
      });
      this.muteGroupKey = String(muteGroupKey);
      const coordinatorId = this.coordinatorId;
      this.visibilityObserver = new IntersectionObserver(entries => {
        const entry = entries[entries.length - 1];
        coordinator.reportRatio(coordinatorId, entry.intersectionRatio, entry.boundingClientRect.top);
      }, { threshold: AUTOPLAY_VISIBILITY_THRESHOLDS });
      this.visibilityObserver.observe(this.$el);
      READER_GESTURE_EVENTS.forEach(type => this.$el.addEventListener(type, this.noteReaderGesture, true));
    },
    noteReaderGesture() {
      this.lastReaderGestureAt = Date.now();
    },
    leaveAutoplayCoordinator() {
      if (this.visibilityObserver) {
        this.visibilityObserver.disconnect();
        this.visibilityObserver = null;
        READER_GESTURE_EVENTS.forEach(type => this.$el.removeEventListener(type, this.noteReaderGesture, true));
      }
      if (this.coordinatorId) {
        useAutoplayCoordinatorStore().unregister(this.coordinatorId);
        this.coordinatorId = null;
        this.coordinatorActive = false;
      }
    },
    onPlay() {
      if (!this.coordinatorId || this.coordinatorActive) {
        return;
      }
      if (Date.now() - this.lastReaderGestureAt <= READER_GESTURE_WINDOW_MS) {
        useAutoplayCoordinatorStore().claim(this.coordinatorId);
        return;
      }
      this.$refs.player.pauseVideo();
    },
    onVolumeChange(payload) {
      if (!this.coordinatorId || payload.isMuted === this.coordinatorMuted) {
        return;
      }
      useAutoplayCoordinatorStore().setGroupMuted(this.muteGroupKey, payload.isMuted);
    },
  },
};
</script>
